/**
 * @fileoverview Core TypeScript interfaces and types for Sandbox Platform SDK
 */

/**
 * Supported technology stacks for sandboxes
 */
export enum TechStack {
  NODEJS = 'nodejs',
  PYTHON = 'python',
  GO = 'go',
  JAVA = 'java',
  REACT_VITE = 'react-vite',
  VUE_VITE = 'vue-vite',
  NEXTJS = 'nextjs',
  SVELTEKIT = 'sveltekit',
  ASTRO = 'astro',
  REMIX = 'remix',
  QWIK = 'qwik',
  SLIDEV = 'slidev',
  EXPO = 'expo',
  NESTJS_ENTERPRISE = 'nestjs-enterprise',
  NEXTJS_SHADCN = 'nextjs-shadcn',
  VITE_SHADCN = 'vite-shadcn',
  VANILLA_VITE = 'vanilla-vite',
  VUEJS = 'vuejs'
}

/**
 * Sandbox status enumeration
 */
export enum SandboxStatus {
  CREATING = 'creating',
  PROVISIONING = 'provisioning',
  RUNNING = 'running',
  STOPPING = 'stopping',
  STOPPED = 'stopped',
  FAILED = 'failed',
  ERROR = 'error'
}

/**
 * Configuration for creating a new sandbox
 */
export interface SandboxConfig {
  /** Technology stack to use */
  techStack: TechStack | string
  /** Template ID to use (optional) */
  template?: string
  /** CPU limit in millicores (optional) */
  cpuLimit?: number
  /** Memory limit in MB (optional) */
  memoryLimit?: number
  /** Sandbox timeout in seconds (optional) */
  timeout?: number
  /** Environment variables */
  environmentVariables?: Record<string, string>
  /** Initial files to upload */
  initialFiles?: Record<string, string>
}

/**
 * Options for creating a sandbox
 */
export interface CreateSandboxOptions {
  /** User ID (optional, for multi-user scenarios) */
  userId?: string
  /** Project name (optional) */
  projectName?: string
  /** Wait for sandbox to be ready before returning */
  waitForReady?: boolean
  /** Timeout for waiting (seconds) */
  timeout?: number
}

/**
 * Sandbox instance representation
 */
export interface Sandbox {
  /** Unique sandbox identifier */
  id: string
  /** Current sandbox status */
  status: SandboxStatus
  /** Technology stack being used */
  techStack: TechStack | string
  /** Creation timestamp */
  createdAt: string
  /** Last update timestamp */
  updatedAt: string
  /** Available URLs for the sandbox */
  urls: Record<string, SandboxUrl>
  /** Configuration used to create the sandbox */
  config: SandboxConfig
  /** Associated project ID */
  projectId?: string
  /** Associated commit ID */
  commitId?: string
}

/**
 * URL information for sandbox services
 */
export interface SandboxUrl {
  /** The actual URL */
  url: string
  /** Port number */
  port: number
  /** Service name */
  name: string
  /** Service description */
  description: string
  /** Service type */
  type: string
}

/**
 * Result of code execution
 */
export interface ExecutionResult {
  /** Standard output */
  stdout: string
  /** Standard error */
  stderr: string
  /** Exit code */
  exitCode: number
  /** Execution time in milliseconds */
  executionTime: number
  /** Whether the execution timed out */
  timeout: boolean
  /** Combined output (stdout + stderr) */
  output?: string
}

/**
 * Options for code execution
 */
export interface ExecuteCodeOptions {
  /** Programming language (auto-detected if not provided) */
  language?: string
  /** Timeout in seconds */
  timeout?: number
  /** Filename for the code */
  filename?: string
}

/**
 * Options for streaming code execution
 */
export interface StreamExecutionOptions extends ExecuteCodeOptions {
  /** Callback for output chunks */
  onOutput?: (output: string) => void
  /** Callback for error chunks */
  onError?: (error: string) => void
  /** Callback when execution completes */
  onComplete?: (result: ExecutionResult) => void
}

/**
 * File information
 */
export interface FileInfo {
  /** File path */
  path: string
  /** File name */
  name: string
  /** File type */
  type: 'file' | 'directory' | 'symlink'
  /** File size in bytes */
  size: number
  /** Last modified timestamp */
  modifiedAt: string
  /** Whether this is a directory */
  isDirectory: boolean
  /** File permissions (octal string) */
  permissions?: string
  /** File owner */
  owner?: string
  /** MIME type (for files) */
  mimeType?: string
  /** Whether file is readable */
  readable?: boolean
  /** Whether file is writable */
  writable?: boolean
  /** Whether file is executable */
  executable?: boolean
}

/**
 * File operation options
 */
export interface FileOperationOptions {
  /** File encoding (default: 'utf8') */
  encoding?: 'utf8' | 'ascii' | 'base64' | 'hex' | 'binary'
  /** Create directories if they don't exist */
  recursive?: boolean
  /** File permissions (octal string) */
  mode?: string
  /** Overwrite existing file */
  overwrite?: boolean
}

/**
 * Directory watching options
 */
export interface WatchOptions {
  /** Watch subdirectories recursively */
  recursive?: boolean
  /** File patterns to include (glob patterns) */
  include?: string[]
  /** File patterns to exclude (glob patterns) */
  exclude?: string[]
  /** Debounce delay in milliseconds */
  debounceMs?: number
}

/**
 * File system change event
 */
export interface FileChangeEvent {
  /** Event type */
  type: 'created' | 'modified' | 'deleted' | 'renamed'
  /** File path */
  path: string
  /** Previous path (for rename events) */
  oldPath?: string
  /** Timestamp */
  timestamp: string
  /** File stats (if available) */
  stats?: FileInfo
}

/**
 * Bulk operation progress
 */
export interface OperationProgress {
  /** Current item being processed */
  current: number
  /** Total items to process */
  total: number
  /** Current file being processed */
  currentFile?: string
  /** Bytes processed */
  bytesProcessed: number
  /** Total bytes to process */
  totalBytes: number
  /** Percentage complete (0-100) */
  percentage: number
  /** Estimated time remaining (ms) */
  estimatedTimeRemaining?: number
}

/**
 * Bulk upload/download options
 */
export interface BulkOperationOptions {
  /** Progress callback */
  onProgress?: (progress: OperationProgress) => void
  /** Parallel operation limit */
  concurrency?: number
  /** Skip existing files */
  skipExisting?: boolean
  /** File operation options */
  fileOptions?: FileOperationOptions
}

/**
 * Job progress information
 */
export interface JobProgress {
  /** Job identifier */
  jobId: string
  /** Current job status */
  status: string
  /** Progress percentage (0-100) */
  progress: number
  /** Progress message */
  message?: string
  /** Associated sandbox ID */
  sandboxId?: string
  /** Error message if failed */
  error?: string
}

/**
 * Authentication configuration
 */
export interface AuthConfig {
  /** API key for authentication */
  apiKey?: string
  /** JWT token for authentication */
  jwtToken?: string
  /** Base URL for API requests */
  baseUrl?: string
}

/**
 * SDK client configuration
 */
export interface SandboxClientConfig {
  /** Authentication configuration */
  auth?: AuthConfig
  /** Base URL for API requests */
  baseUrl?: string
  /** Request timeout in milliseconds */
  timeout?: number
  /** Number of retry attempts */
  retryAttempts?: number
  /** Delay between retries in milliseconds */
  retryDelay?: number
  /** Enable debug logging */
  debug?: boolean
}

/**
 * WebSocket event types
 */
export enum WebSocketEventType {
  SANDBOX_STATUS = 'sandbox-status',
  CODE_OUTPUT = 'code-output',
  CODE_ERROR = 'code-error',
  EXECUTION_COMPLETE = 'execution-complete',
  FILE_CHANGE = 'file-change',
  FILE_UPLOAD_PROGRESS = 'file-upload-progress',
  FILE_DOWNLOAD_PROGRESS = 'file-download-progress',
  BULK_OPERATION_PROGRESS = 'bulk-operation-progress',
  DIRECTORY_WATCH_START = 'directory-watch-start',
  DIRECTORY_WATCH_STOP = 'directory-watch-stop',
  JOB_PROGRESS = 'job-progress'
}

/**
 * WebSocket message structure
 */
export interface WebSocketMessage {
  /** Event type */
  event: WebSocketEventType
  /** Event data */
  data: any
  /** Associated sandbox ID */
  sandboxId?: string
  /** Message timestamp */
  timestamp?: string
}

/**
 * Error response from API
 */
export interface ApiError {
  /** Error message */
  error: string
  /** Error details */
  details?: any
  /** HTTP status code */
  status?: number
}

/**
 * API response wrapper
 */
export interface ApiResponse<T = any> {
  /** Whether the request was successful */
  success: boolean
  /** Response data (if successful) */
  data?: T
  /** Error message (if failed) */
  error?: string
  /** Additional error details */
  details?: any
}